import { List as ImmutableList } from 'immutable';
import { PatternType } from '../constants-types/Enums';
import { List } from '../constants-types/PiList.model';
import { IconType } from '../core-data-model/IconType';

/**
 * Information on particular reference pattern types.
 */
export type ReferencePatternType = {
    id: PatternType;
    /**
     * Used to piece together translation keys.
     *
     * Should not be used in plain text for the user to see as it could be
     * different than what the actual name of the pattern type is as of 6/29/2022.
     * (Also it isn't translated, so there's that too.)
     */
    keyFragment: string;
    /**
     * Seems to be used only on the database side.
     */
    internalName: string;
    tooltipKey: string;
    key: string;
    icon: {
        src: string;
        cssClass?: string;
        abstractIconSVG?: IconType;
    };
    jobIcon: {
        src: string;
        cssClass?: string;
    };
    rangeMidpointValues: {
        A: number;
        B: number;
        C: number;
        D: number;
    };
    standardizedRanges: {
        A: number;
        B: number;
        C: number;
        D: number;
    };
    /**
     * This is not currently used anywhere in the product besides tests + mocks.
     * Future use of this key should be avoided. The current location for
     * reference pattern PDFs is Assets in pi-spa as of 11/10/2022.
     *
     * Historically, this was used to point to assets that were stored in
     * SalesForce, but that is no longer the practice at this time.
     */
    pdfLocationKey: string;
};

// TODO :  Hookup to the actual APIs once they are complete
export const ReferencePatternsInformation: List<ReferencePatternType> =
    ImmutableList([
        {
            id: PatternType.Adapter,
            keyFragment: 'adapter',
            internalName: 'Chameleon',
            key: 'toolkit.refpattern.name.adapter',
            tooltipKey: 'referencePattern.adapter-tooltip',
            icon: {
                src: '/Assets/ReferencePatterns/adapterBadge.png',
                cssClass: 'dashboard_referenceInfo--icon',
                abstractIconSVG: IconType.Adapter
            },
            jobIcon: {
                src: '/Assets/ReferencePatterns/job-adapterBadge.png',
                cssClass: 'dashboard_referenceInfo--icon'
            },
            rangeMidpointValues: {
                A: 0,
                B: 0,
                C: 0,
                D: 0
            },
            standardizedRanges: {
                A: 0,
                B: 0,
                C: 0,
                D: 0
            },
            pdfLocationKey: 'referenceProfileCard.referencePattern.pdf.adapter'
        },
        {
            id: PatternType.Venturer,
            keyFragment: 'venturer',
            internalName: 'Venturer',
            key: 'toolkit.refpattern.name.venturer',
            tooltipKey: 'referencePattern.venturer-tooltip',
            icon: {
                src: '/Assets/ReferencePatterns/venturerBadge.png',
                cssClass: 'dashboard_referenceInfo--icon',
                abstractIconSVG: IconType.Venturer
            },
            jobIcon: {
                src: '/Assets/ReferencePatterns/job-venturerBadge.png',
                cssClass: 'dashboard_referenceInfo--icon'
            },
            rangeMidpointValues: {
                A: 1.3,
                B: -0.44,
                C: 0.42,
                D: -0.43
            },
            standardizedRanges: {
                A: 7.0,
                B: -1.94,
                C: -3.74,
                D: -1.26
            },
            pdfLocationKey: 'referenceProfileCard.referencePattern.pdf.venturer'
        },
        {
            id: PatternType.SciPro,
            keyFragment: 'strategist',
            internalName: 'ScientificProfessional',
            key: 'toolkit.refpattern.name.scientificprofessional',
            tooltipKey: 'referencePattern.strategist-tooltip',
            icon: {
                src: '/Assets/ReferencePatterns/strategistBadge.png',
                cssClass: 'dashboard_referenceInfo--icon',
                abstractIconSVG: IconType.Strategist
            },
            jobIcon: {
                src: '/Assets/ReferencePatterns/job-strategistBadge.png',
                cssClass: 'dashboard_referenceInfo--icon'
            },
            rangeMidpointValues: {
                A: 1.02,
                B: -0.35,
                C: -0.97,
                D: 0.3
            },
            standardizedRanges: {
                A: 6.53,
                B: -2.36,
                C: -6.26,
                D: 2.09
            },
            pdfLocationKey:
                'referenceProfileCard.referencePattern.pdf.strategist'
        },
        {
            id: PatternType.CreativeAnalytical,
            keyFragment: 'analyzer',
            internalName: 'CreativeAnalyst',
            key: 'toolkit.refpattern.name.creativeanalytical',
            tooltipKey: 'referencePattern.analyzer-tooltip',
            icon: {
                src: '/Assets/ReferencePatterns/analyzerBadge.png',
                cssClass: 'dashboard_referenceInfo--icon',
                abstractIconSVG: IconType.Analyzer
            },
            jobIcon: {
                src: '/Assets/ReferencePatterns/job-analyzerBadge.png',
                cssClass: 'dashboard_referenceInfo--icon'
            },
            rangeMidpointValues: {
                A: 0.77,
                B: -0.92,
                C: -0.6,
                D: 0.74
            },
            standardizedRanges: {
                A: 4.66,
                B: -4.45,
                C: -5.08,
                D: 4.86
            },
            pdfLocationKey: 'referenceProfileCard.referencePattern.pdf.analyzer'
        },
        {
            id: PatternType.Control,
            keyFragment: 'controller',
            internalName: 'Control',
            key: 'toolkit.refpattern.name.control',
            tooltipKey: 'referencePattern.controller-tooltip',
            icon: {
                src: '/Assets/ReferencePatterns/controllerBadge.png',
                cssClass: 'dashboard_referenceInfo--icon',
                abstractIconSVG: IconType.Controller
            },
            jobIcon: {
                src: '/Assets/ReferencePatterns/job-controllerBadge.png',
                cssClass: 'dashboard_referenceInfo--icon'
            },
            rangeMidpointValues: {
                A: 0.25,
                B: -0.07,
                C: -1.2,
                D: 1.01
            },
            standardizedRanges: {
                A: 1.94,
                B: -2.48,
                C: -6.09,
                D: 6.62
            },
            pdfLocationKey:
                'referenceProfileCard.referencePattern.pdf.controller'
        },
        {
            id: PatternType.Specialist,
            keyFragment: 'specialist',
            internalName: 'Specialist',
            key: 'toolkit.refpattern.name.specialist',
            tooltipKey: 'referencePattern.specialist-tooltip',
            icon: {
                src: '/Assets/ReferencePatterns/specialistBadge.png',
                cssClass: 'dashboard_referenceInfo--icon',
                abstractIconSVG: IconType.Specialist
            },
            jobIcon: {
                src: '/Assets/ReferencePatterns/job-specialistBadge.png',
                cssClass: 'dashboard_referenceInfo--icon'
            },
            rangeMidpointValues: {
                A: -0.34,
                B: -0.48,
                C: -0.35,
                D: 1.18
            },
            standardizedRanges: {
                A: -1.39,
                B: -3.43,
                C: -2.12,
                D: 7.0
            },
            pdfLocationKey:
                'referenceProfileCard.referencePattern.pdf.specialist'
        },
        {
            id: PatternType.Scholar,
            keyFragment: 'scholar',
            internalName: 'Scholar',
            key: 'toolkit.refpattern.name.scholar',
            tooltipKey: 'referencePattern.scholar-tooltip',
            icon: {
                src: '/Assets/ReferencePatterns/scholarBadge.png',
                cssClass: 'dashboard_referenceInfo--icon',
                abstractIconSVG: IconType.Scholar
            },
            jobIcon: {
                src: '/Assets/ReferencePatterns/job-scholarBadge.png',
                cssClass: 'dashboard_referenceInfo--icon'
            },
            rangeMidpointValues: {
                A: 0.32,
                B: -1.16,
                C: 0.16,
                D: 0.67
            },
            standardizedRanges: {
                A: 1.17,
                B: -7.0,
                C: 1.43,
                D: 4.31
            },
            pdfLocationKey: 'referenceProfileCard.referencePattern.pdf.scholar'
        },
        {
            id: PatternType.Individualist,
            keyFragment: 'individualist',
            internalName: 'Individualist',
            key: 'toolkit.refpattern.name.individualist',
            tooltipKey: 'referencePattern.individualist-tooltip',
            icon: {
                src: '/Assets/ReferencePatterns/individualistBadge.png',
                cssClass: 'dashboard_referenceInfo--icon',
                abstractIconSVG: IconType.Individualist
            },
            jobIcon: {
                src: '/Assets/ReferencePatterns/job-individualistBadge.png',
                cssClass: 'dashboard_referenceInfo--icon'
            },
            rangeMidpointValues: {
                A: 0.55,
                B: -0.5,
                C: 0.69,
                D: 0.74
            },
            standardizedRanges: {
                A: 4.08,
                B: -4.23,
                C: 4.63,
                D: -4.48
            },
            pdfLocationKey:
                'referenceProfileCard.referencePattern.pdf.individualist'
        },
        {
            id: PatternType.Authoritative,
            keyFragment: 'captain',
            internalName: 'AuthoritativeManagement',
            key: 'toolkit.refpattern.name.authoritative',
            tooltipKey: 'referencePattern.captain-tooltip',
            icon: {
                src: '/Assets/ReferencePatterns/captainBadge.png',
                cssClass: 'dashboard_referenceInfo--icon',
                abstractIconSVG: IconType.Captain
            },
            jobIcon: {
                src: '/Assets/ReferencePatterns/job-captainBadge.png',
                cssClass: 'dashboard_referenceInfo--icon'
            },
            rangeMidpointValues: {
                A: 1.24,
                B: 0.7,
                C: -1.44,
                D: -0.5
            },
            standardizedRanges: {
                A: 6.25,
                B: 2.92,
                C: -6.43,
                D: -2.75
            },
            pdfLocationKey: 'referenceProfileCard.referencePattern.pdf.captain'
        },
        {
            id: PatternType.Maverick,
            keyFragment: 'maverick',
            internalName: 'Maverick',
            key: 'toolkit.refpattern.name.maverick',
            tooltipKey: 'referencePattern.maverick-tooltip',
            icon: {
                src: '/Assets/ReferencePatterns/maverickBadge.png',
                cssClass: 'dashboard_referenceInfo--icon',
                abstractIconSVG: IconType.Maverick
            },
            jobIcon: {
                src: '/Assets/ReferencePatterns/job-maverickBadge.png',
                cssClass: 'dashboard_referenceInfo--icon'
            },
            rangeMidpointValues: {
                A: 1.07,
                B: 0.75,
                C: -0.49,
                D: -1.34
            },
            standardizedRanges: {
                A: 6.25,
                B: 3.07,
                C: -2.94,
                D: -6.39
            },
            pdfLocationKey: 'referenceProfileCard.referencePattern.pdf.maverick'
        },
        {
            id: PatternType.Persuasive,
            keyFragment: 'persuader',
            internalName: 'PersuasiveManagement',
            key: 'toolkit.refpattern.name.persuasive',
            tooltipKey: 'referencePattern.persuader-tooltip',
            icon: {
                src: '/Assets/ReferencePatterns/persuaderBadge.png',
                cssClass: 'dashboard_referenceInfo--icon',
                abstractIconSVG: IconType.Persuader
            },
            jobIcon: {
                src: '/Assets/ReferencePatterns/job-persuaderBadge.png',
                cssClass: 'dashboard_referenceInfo--icon'
            },
            rangeMidpointValues: {
                A: 0.38,
                B: 1.11,
                C: -1.05,
                D: -0.44
            },
            standardizedRanges: {
                A: 2.56,
                B: 6.4,
                C: -6.29,
                D: -2.67
            },
            pdfLocationKey:
                'referenceProfileCard.referencePattern.pdf.persuader'
        },
        {
            id: PatternType.AltruisticService,
            keyFragment: 'altruist',
            internalName: 'AltruisticService',
            key: 'toolkit.refpattern.name.altruisticservice',
            tooltipKey: 'referencePattern.altruist-tooltip',
            icon: {
                src: '/Assets/ReferencePatterns/altruisticServiceBadge.png',
                cssClass: 'dashboard_referenceInfo--icon',
                abstractIconSVG: IconType.Altruist
            },
            jobIcon: {
                src: '/Assets/ReferencePatterns/job-altruisticServiceBadge.png',
                cssClass: 'dashboard_referenceInfo--icon'
            },
            rangeMidpointValues: {
                A: -0.69,
                B: 0.65,
                C: -0.53,
                D: 0.57
            },
            standardizedRanges: {
                A: -4.57,
                B: 4.23,
                C: -4.2,
                D: 4.53
            },
            pdfLocationKey: 'referenceProfileCard.referencePattern.pdf.altruist'
        },
        {
            id: PatternType.SocialInterest,
            keyFragment: 'collaborator',
            internalName: 'SocialInterest',
            key: 'toolkit.refpattern.name.socialinterest',
            tooltipKey: 'referencePattern.collaborator-tooltip',
            icon: {
                src: '/Assets/ReferencePatterns/collaboratorBadge.png',
                cssClass: 'dashboard_referenceInfo--icon',
                abstractIconSVG: IconType.Collaborator
            },
            jobIcon: {
                src: '/Assets/ReferencePatterns/job-collaboratorBadge.png',
                cssClass: 'dashboard_referenceInfo--icon'
            },
            rangeMidpointValues: {
                A: -0.73,
                B: 0.45,
                C: 0.84,
                D: -0.56
            },
            standardizedRanges: {
                A: -4.95,
                B: 2.69,
                C: 6.28,
                D: -4.02
            },
            pdfLocationKey:
                'referenceProfileCard.referencePattern.pdf.collaborator'
        },
        {
            id: PatternType.Promotional,
            keyFragment: 'promoter',
            internalName: 'Promotional',
            key: 'toolkit.refpattern.name.promotional',
            tooltipKey: 'referencePattern.promoter-tooltip',
            icon: {
                src: '/Assets/ReferencePatterns/promoterBadge.png',
                cssClass: 'dashboard_referenceInfo--icon',
                abstractIconSVG: IconType.Promoter
            },
            jobIcon: {
                src: '/Assets/ReferencePatterns/job-promoterBadge.png',
                cssClass: 'dashboard_referenceInfo--icon'
            },
            rangeMidpointValues: {
                A: -0.38,
                B: 1.13,
                C: -0.14,
                D: -0.61
            },
            standardizedRanges: {
                A: -2.18,
                B: 7.0,
                C: -1.16,
                D: -3.59
            },
            pdfLocationKey: 'referenceProfileCard.referencePattern.pdf.promoter'
        },
        {
            id: PatternType.Diligence,
            keyFragment: 'guardian',
            internalName: 'Diligence',
            key: 'toolkit.refpattern.name.diligence',
            tooltipKey: 'referencePattern.guardian-tooltip',
            icon: {
                src: '/Assets/ReferencePatterns/guardianBadge.png',
                cssClass: 'dashboard_referenceInfo--icon',
                abstractIconSVG: IconType.Guardian
            },
            jobIcon: {
                src: '/Assets/ReferencePatterns/job-guardianBadge.png',
                cssClass: 'dashboard_referenceInfo--icon'
            },
            rangeMidpointValues: {
                A: -1.24,
                B: -0.44,
                C: 0.39,
                D: 1.29
            },
            standardizedRanges: {
                A: -6.41,
                B: -2.66,
                C: 2.78,
                D: 6.28
            },
            pdfLocationKey: 'referenceProfileCard.referencePattern.pdf.guardian'
        },
        {
            id: PatternType.Operational,
            keyFragment: 'operator',
            internalName: 'Operational',
            key: 'toolkit.refpattern.name.operational',
            tooltipKey: 'referencePattern.operator-tooltip',
            icon: {
                src: '/Assets/ReferencePatterns/operatorBadge.png',
                cssClass: 'dashboard_referenceInfo--icon',
                abstractIconSVG: IconType.Operator
            },
            jobIcon: {
                src: '/Assets/ReferencePatterns/job-operatorBadge.png',
                cssClass: 'dashboard_referenceInfo--icon'
            },
            rangeMidpointValues: {
                A: -1.05,
                B: -0.39,
                C: 1.12,
                D: 0.32
            },
            standardizedRanges: {
                A: 6.47,
                B: -2.6,
                C: 6.28,
                D: 2.79
            },
            pdfLocationKey: 'referenceProfileCard.referencePattern.pdf.operator'
        },
        {
            id: PatternType.Artisan,
            keyFragment: 'craftsman',
            /**
             * At the time of updating Craftsman to Artisan, it didn't look
             * like the internalName was used anywhere on the frontend, but
             * it wasn't clear if it was used on the backend somwhere as this
             * value gets used on the database it seems. 6/29/2022.
             */
            internalName: 'Craftsman',
            key: 'toolkit.refpattern.name.craftsman',
            tooltipKey: 'referencePattern.craftsman-tooltip',
            icon: {
                src: '/Assets/ReferencePatterns/artisanBadge.png',
                cssClass: 'dashboard_referenceInfo--icon',
                abstractIconSVG: IconType.Artisan
            },
            jobIcon: {
                src: '/Assets/ReferencePatterns/job-artisanBadge.png',
                cssClass: 'dashboard_referenceInfo--icon'
            },
            rangeMidpointValues: {
                A: -0.54,
                B: -1.1,
                C: 0.92,
                D: 0.72
            },
            standardizedRanges: {
                A: -3.22,
                B: -6.16,
                C: 4.4,
                D: 4.98
            },
            pdfLocationKey: 'referenceProfileCard.referencePattern.pdf.artisan'
        }
    ]);
